import React, { useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useToast } from '@raptormaps/toast';
import { Icon } from '@raptormaps/icon';
import { Text } from '@raptormaps/text';
import { getOrgDetailsQuery } from '../../api/services/orgs';
import {
  getCreatedFeaturesQuery,
  getOrgFeaturesQuery,
} from '../../api/services/features';
import { OrgSearchInput } from '../../components/OrgDetails/OrgSearchInput';
import { OrgPageHeader } from '../../components/OrgDetails/OrgPageHeader';
import { OrgIdentificationCard } from '../../components/OrgDetails/OrgIdentificationCard';
import { OrgFeaturePermissionsCard } from '../../components/OrgDetails/OrgFeaturePermissionsCard';
import { EditOrgModal } from '../../components/OrgDetails/EditOrgModal';
import {
  PageWrapper,
  EmptyState,
} from '../../components/OrgDetails/OrgDetails.styles';
import theme from '@raptormaps/theme';

export function OrgDetails() {
  const toast = useToast();
  const history = useHistory();
  const location = useLocation();

  const orgIdParam = new URLSearchParams(location.search).get('org_id');
  const orgId = orgIdParam ? parseInt(orgIdParam, 10) : null;

  // Fetch org details when orgId is set
  const {
    data: orgDetailsData,
    isLoading: isOrgDetailsLoading,
    refetch: refetchOrgDetails,
  } = useQuery({
    ...getOrgDetailsQuery(orgId ?? 0),
    enabled: !!orgId,
    onError: () => {
      toast.error('Failed to load organization details.', {
        actionOptions: {
          label: 'Try again',
          onClick: () => refetchOrgDetails(),
        },
      });
    },
  });

  // Always fetch created features
  const {
    data: createdFeaturesData,
    isLoading: isFeaturesLoading,
    refetch: refetchCreatedFeatures,
  } = useQuery({
    ...getCreatedFeaturesQuery(),
    onError: () => {
      toast.error('Failed to load feature permissions.', {
        actionOptions: {
          label: 'Try again',
          onClick: () => refetchCreatedFeatures(),
        },
      });
    },
  });

  // Fetch org features when orgId is set
  const {
    data: orgFeaturesData,
    isLoading: isOrgFeaturesLoading,
    refetch: refetchOrgFeatures,
  } = useQuery({
    ...getOrgFeaturesQuery(orgId ?? 0),
    enabled: !!orgId,
    onError: () => {
      toast.error('Failed to load organization features.', {
        actionOptions: {
          label: 'Try again',
          onClick: () => refetchOrgFeatures(),
        },
      });
    },
  });

  const orgFeatureIds = useMemo(
    () =>
      new Set<number>(
        orgFeaturesData?.org_features?.map(f => f.feature_id) ?? [],
      ),
    [orgFeaturesData],
  );

  const selectedOrg = orgDetailsData?.org ?? null;
  const selectedOrgName = selectedOrg?.name;

  const handleSelect = useCallback(
    (selectedOrgId: number) => {
      history.push({ search: `?org_id=${selectedOrgId}` });
    },
    [history],
  );

  const handleClear = useCallback(() => {
    history.push({ search: '' });
  }, [history]);

  const [editModalOpen, setEditModalOpen] = useState(false);

  return (
    <PageWrapper>
      <OrgSearchInput
        onSelect={handleSelect}
        onClear={handleClear}
        selectedOrgName={selectedOrgName}
      />

      {!orgId ? (
        <EmptyState>
          <Icon
            icon="MagnifyingGlass"
            size="large"
            color={theme.colors.neutral_400}
          />
          <Text variant="paragraph_medium" color={theme.colors.neutral_600}>
            Search and select an organization above to view its details
          </Text>
        </EmptyState>
      ) : (
        <>
          {selectedOrg && (
            <OrgPageHeader
              orgName={selectedOrg.name}
              onEdit={() => setEditModalOpen(true)}
            />
          )}

          <OrgIdentificationCard
            org={selectedOrg ?? { id: 0, name: '', paid: false }}
            isLoading={isOrgDetailsLoading}
          />

          <OrgFeaturePermissionsCard
            orgId={orgId}
            features={createdFeaturesData?.features ?? []}
            orgFeatureIds={orgFeatureIds}
            isLoading={isFeaturesLoading || isOrgFeaturesLoading}
          />
        </>
      )}
      {orgId && selectedOrg && (
        <EditOrgModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          orgId={orgId}
          currentName={selectedOrg.name}
        />
      )}
    </PageWrapper>
  );
}
